// © 2025 Camp Promethean, LLC. All Rights Reserved.
// Selections Panel Component

import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useSelections } from '@/hooks/useSelections';
import { SelectionCard } from './SelectionCard';
import { EnhancedSelection } from '@/types/selection';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, Filter, SlidersHorizontal, Search, Plus } from 'lucide-react';
import { calculatePercentile } from '@/utils/selectionUtils';

interface SelectionsPanelProps {
  projectId: string;
  onCreateSelection?: () => void;
  onEditSelection?: (selection: EnhancedSelection) => void;
  onDeleteSelection?: (selection: EnhancedSelection) => void;
  onViewSelectionDetails?: (selection: EnhancedSelection) => void;
  onCompareSelections?: (selections: EnhancedSelection[]) => void;
}

export function SelectionsPanel({
  projectId,
  onCreateSelection,
  onEditSelection,
  onDeleteSelection,
  onViewSelectionDetails,
  onCompareSelections
}: SelectionsPanelProps) {
  // Get selections data
  const { 
    selections, 
    enhancedSelections, 
    plants,
    sensoryData,
    isLoading, 
    error 
  } = useSelections(projectId);
  
  // Local state for filters and sorting
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('dateDesc');
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [filterGeneration, setFilterGeneration] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  
  // Calculate percentile data for metrics
  const percentileData = useMemo(() => {
    // Exit early if no enhanced selections
    if (!enhancedSelections?.length) return {};
    
    // Build datasets for each metric
    const metrics: Record<string, number[]> = {
      structure: [],
      vigor: [],
      yield: [],
      frost: [],
      overall: [],
      aroma_intensity: [],
      aroma_quality: [],
      flavor_intensity: [],
      flavor_quality: [],
      smoke_harshness: []
    };
    
    // Collect all metric values
    enhancedSelections.forEach(selection => {
      // Growth metrics
      if (selection.growthMetrics) {
        metrics.structure.push(selection.growthMetrics.structure);
        metrics.vigor.push(selection.growthMetrics.vigor);
        metrics.yield.push(selection.growthMetrics.yield);
        metrics.frost.push(selection.growthMetrics.frost);
        metrics.overall.push(selection.growthMetrics.overall);
      }
      
      // Sensory metrics
      if (selection.sensoryData) {
        if (selection.sensoryData.aroma_intensity) {
          metrics.aroma_intensity.push(selection.sensoryData.aroma_intensity);
        }
        if (selection.sensoryData.aroma_quality) {
          metrics.aroma_quality.push(selection.sensoryData.aroma_quality);
        }
        if (selection.sensoryData.flavor_intensity) {
          metrics.flavor_intensity.push(selection.sensoryData.flavor_intensity);
        }
        if (selection.sensoryData.flavor_quality) {
          metrics.flavor_quality.push(selection.sensoryData.flavor_quality);
        }
        if (selection.sensoryData.smoke_harshness) {
          metrics.smoke_harshness.push(selection.sensoryData.smoke_harshness);
        }
      }
    });
    
    return metrics;
  }, [enhancedSelections]);
  
  // Get all unique generations
  const generations = useMemo(() => {
    if (!enhancedSelections?.length) return [];
    const gens = new Set<string>();
    enhancedSelections.forEach(selection => {
      if (selection.generation) gens.add(selection.generation);
    });
    return Array.from(gens).sort();
  }, [enhancedSelections]);
  
  // Get all unique tags for filtering
  const allTags = useMemo(() => {
    if (!enhancedSelections?.length) return [];
    const tags = new Set<string>();
    enhancedSelections.forEach(selection => {
      selection.tags.forEach(tag => tags.add(tag.name));
    });
    return Array.from(tags).sort();
  }, [enhancedSelections]);
  
  // Filter and sort selections
  const filteredSelections = useMemo(() => {
    if (!enhancedSelections) return [];
    
    // Start with all selections
    let result = [...enhancedSelections];
    
    // Filter by tab
    if (activeTab === 'top') {
      result = result.filter(s => 
        s.growthMetrics.overall >= 8 || 
        (s.sensoryData && (s.sensoryData.aroma_quality >= 8 || s.sensoryData.flavor_quality >= 8))
      );
    } else if (activeTab === 'recent') {
      // Sort by date and take the 10 most recent
      result = [...result].sort((a, b) => 
        new Date(b.selectionDate).getTime() - new Date(a.selectionDate).getTime()
      ).slice(0, 10);
    }
    
    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(s => 
        s.plantId.toLowerCase().includes(query) ||
        s.selectionRationale?.toLowerCase().includes(query) ||
        s.selectionNotes?.toLowerCase().includes(query) ||
        s.selectionHighlights?.some(h => h.toLowerCase().includes(query)) ||
        s.tags.some(tag => tag.name.toLowerCase().includes(query))
      );
    }
    
    // Apply generation filter
    if (filterGeneration !== 'all') {
      result = result.filter(s => s.generation === filterGeneration);
    }
    
    // Apply tag filters
    if (filterTags.length > 0) {
      result = result.filter(s => 
        filterTags.every(tag => 
          s.tags.some(t => t.name.toLowerCase() === tag.toLowerCase())
        )
      );
    }
    
    // Apply sorting
    switch (sortBy) {
      case 'dateAsc':
        result.sort((a, b) => new Date(a.selectionDate).getTime() - new Date(b.selectionDate).getTime());
        break;
      case 'dateDesc':
        result.sort((a, b) => new Date(b.selectionDate).getTime() - new Date(a.selectionDate).getTime());
        break;
      case 'overall':
        result.sort((a, b) => b.growthMetrics.overall - a.growthMetrics.overall);
        break;
      case 'yield':
        result.sort((a, b) => b.growthMetrics.yield - a.growthMetrics.yield);
        break;
      case 'structure':
        result.sort((a, b) => b.growthMetrics.structure - a.growthMetrics.structure);
        break;
      case 'aroma':
        result.sort((a, b) => {
          const aValue = a.sensoryData?.aroma_quality || 0;
          const bValue = b.sensoryData?.aroma_quality || 0;
          return bValue - aValue;
        });
        break;
      case 'flavor':
        result.sort((a, b) => {
          const aValue = a.sensoryData?.flavor_quality || 0;
          const bValue = b.sensoryData?.flavor_quality || 0;
          return bValue - aValue;
        });
        break;
      default:
        // Default sort by date descending
        result.sort((a, b) => new Date(b.selectionDate).getTime() - new Date(a.selectionDate).getTime());
    }
    
    return result;
  }, [enhancedSelections, searchQuery, sortBy, filterTags, filterGeneration, activeTab]);
  
  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center mb-4">
          <Skeleton className="h-10 w-40" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-64 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Error loading selections: {typeof error === 'string' ? error : (error as Error).message || 'Unknown error'}
        </AlertDescription>
      </Alert>
    );
  }
  
  // Empty state
  if (!enhancedSelections || enhancedSelections.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900">No selections yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating your first selection from a phenotype
          </p>
          {onCreateSelection && (
            <Button 
              onClick={onCreateSelection} 
              className="mt-4"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Selection
            </Button>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Header and controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
        <h2 className="text-xl font-semibold">
          Selections ({filteredSelections.length})
        </h2>
        
        <div className="flex flex-wrap gap-2">
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search selections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full sm:w-48 md:w-64 pl-9"
            />
          </div>
          
          <Button
            variant="outline"
            size="icon"
            onClick={() => setShowFilters(!showFilters)}
            aria-expanded={showFilters}
            aria-label="Toggle filters"
            className={showFilters ? 'bg-primary/5' : ''}
          >
            <Filter className="h-4 w-4" />
          </Button>
          
          {onCreateSelection && (
            <Button onClick={onCreateSelection}>
              <Plus className="mr-2 h-4 w-4" />
              New
            </Button>
          )}
        </div>
      </div>
      
      {/* Tabs */}
      <Tabs 
        defaultValue="all" 
        value={activeTab} 
        onValueChange={setActiveTab}
        className="mb-4"
      >
        <TabsList>
          <TabsTrigger value="all">All Selections</TabsTrigger>
          <TabsTrigger value="top">Top Performers</TabsTrigger>
          <TabsTrigger value="recent">Recent</TabsTrigger>
        </TabsList>
      </Tabs>
      
      {/* Filters */}
      {showFilters && (
        <div className="bg-gray-50 p-4 rounded-lg flex flex-col sm:flex-row gap-4 mb-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Sort by</label>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Sort order" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="dateDesc">Newest first</SelectItem>
                <SelectItem value="dateAsc">Oldest first</SelectItem>
                <SelectItem value="overall">Highest overall rating</SelectItem>
                <SelectItem value="yield">Highest yield</SelectItem>
                <SelectItem value="structure">Best structure</SelectItem>
                <SelectItem value="aroma">Best aroma</SelectItem>
                <SelectItem value="flavor">Best flavor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Generation</label>
            <Select value={filterGeneration} onValueChange={setFilterGeneration}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All generations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All generations</SelectItem>
                {generations.map(gen => (
                  <SelectItem key={gen} value={gen}>{gen}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Filter by tags</label>
            <Select 
              value={filterTags.length > 0 ? 'has-filters' : ''} 
              onValueChange={(value) => {
                if (value && value !== 'has-filters') {
                  setFilterTags(prev => [...prev, value]);
                }
              }}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select tags" />
              </SelectTrigger>
              <SelectContent>
                {filterTags.length === 0 && <SelectItem value="">All tags</SelectItem>}
                {allTags
                  .filter(tag => !filterTags.includes(tag))
                  .map(tag => (
                    <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                  ))
                }
              </SelectContent>
            </Select>
            
            {/* Selected tag filters */}
            {filterTags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {filterTags.map(tag => (
                  <Button
                    key={tag}
                    variant="secondary"
                    size="sm"
                    className="text-xs h-6 px-2 rounded-full"
                    onClick={() => setFilterTags(filterTags.filter(t => t !== tag))}
                  >
                    {tag} ✕
                  </Button>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-6 px-2"
                  onClick={() => setFilterTags([])}
                >
                  Clear all
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Results count */}
      {(searchQuery || filterTags.length > 0 || filterGeneration !== 'all') && (
        <div className="text-sm text-gray-500 mb-4">
          Showing {filteredSelections.length} of {enhancedSelections.length} selections
        </div>
      )}
      
      {/* Selection Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredSelections.map(selection => (
          <SelectionCard
            key={selection.id}
            selection={selection}
            compareSelections={enhancedSelections.filter(s => s.id !== selection.id)}
            percentileData={percentileData}
            onEdit={onEditSelection}
            onDelete={onDeleteSelection}
            onViewDetails={onViewSelectionDetails}
            onCompare={onCompareSelections ? 
              (s) => onCompareSelections([s]) : 
              undefined
            }
          />
        ))}
      </div>
      
      {/* No results after filtering */}
      {enhancedSelections.length > 0 && filteredSelections.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 px-4 bg-gray-50 rounded-lg">
          <AlertCircle className="h-10 w-10 text-gray-400 mb-2" />
          <h3 className="text-lg font-medium text-gray-900">No matching selections</h3>
          <p className="mt-1 text-sm text-gray-500 max-w-md text-center">
            No selections match your current filters. Try adjusting your search criteria or filters.
          </p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={() => {
              setSearchQuery('');
              setFilterTags([]);
              setFilterGeneration('all');
            }}
          >
            Clear all filters
          </Button>
        </div>
      )}
    </div>
  );
} 